/**
 * What you carry, at the top right: wood, stone and crystal with their counts, and any blocks you
 * hold; a count at its limit (the room in your pockets, rules 21) shows in the warning colour. In
 * admin mode (rules 26) nothing runs out: the chip says so, ∞, instead of counting.
 */
import {
  BLOCKS,
  ITEMS,
  RESOURCES,
  ROOM,
  type Item,
  type Pockets,
} from '@bramvasterd/terrella-rules';
import { useEffect, useRef, useState, type JSX } from 'react';

import { adminOf, pocketsOf } from '@game/planet/pockets';
import { t } from '@i18n/index';
import { useGame, useGameState, useLocale } from '@ui/hooks';
import { ResourceIcon } from '@ui/icons';

import './pockets.css';

const BUMP_MS = 650;

export function PocketsChip(): JSX.Element | null {
  useLocale();
  const game = useGame();
  const me = useGameState((s) => s.me);
  useGameState((s) => s.planetVersion);
  const pockets = pocketsOf(game.store.get().view, me?.id ?? null);
  const [bumped, setBumped] = useState<Item | null>(null);
  const previous = useRef<Readonly<Pockets> | null>(null);
  // a count that rose bounces for a moment
  useEffect(() => {
    const was = previous.current;
    previous.current = pockets;
    if (!was) return;
    const rose = ITEMS.find((r) => pockets[r] > was[r]);
    if (rose === undefined) return;
    setBumped(rose);
    const timer = setTimeout(() => setBumped(null), BUMP_MS);
    return () => clearTimeout(timer);
  }, [pockets]);
  if (!me) return null;
  if (adminOf(game.store.get().view, me.id))
    return (
      <div
        id="pockets"
        className="glass pockets admin"
        role="group"
        aria-label={t('hud.pockets-admin-aria')}
      >
        <span className="res" data-admin>
          <b>∞</b>
          {t('hud.admin')}
        </span>
      </div>
    );
  const shown: Item[] = [...RESOURCES, ...BLOCKS.filter((block) => pockets[block] > 0)];
  return (
    <div
      id="pockets"
      className="glass pockets"
      role="group"
      aria-label={t('hud.pockets-aria', pockets)}
    >
      {shown.map((item) => (
        <span
          key={item}
          className={`res${bumped === item ? ' bump' : ''}${pockets[item] >= ROOM[item] ? ' full' : ''}`}
          data-resource={item}
          title={`${String(pockets[item])}/${String(ROOM[item])}`}
        >
          <ResourceIcon kind={item} />
          <b>{pockets[item]}</b>
        </span>
      ))}
    </div>
  );
}
